/**
 * RICE Scoring tool runner.
 * Implements the RICE formula: (Reach × Impact × Confidence) / Effort
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
  AttributePrimitive,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for RICE.
 */
export function getRiceSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("rice-scoring");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the RICE scoring tool.
 * 
 * Formula: RICE = (reach * impact * (confidence / 100)) / effort
 * 
 * @param items - Items to score
 * @param _criteriaSet - CriteriaSet (optional, not used by RICE)
 * @param toolConfig - Tool configuration (confidence_input_scale)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runRiceTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  getRiceSpec();
  const toolRunId = `rice-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get confidence scale from config (default: 0_to_100)
  const confidenceScale = (toolConfig.confidence_input_scale as string) || "0_to_100";
  const confidenceDivisor = confidenceScale === "0_to_1" ? 1 : 100;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    // Use the arrange validation applyLens function
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Score each item
  const scoredItems: Array<{
    item_id: string;
    score: number;
    rank: number;
    raw_reach: AttributePrimitive;
    raw_impact: AttributePrimitive;
    raw_confidence: AttributePrimitive;
    raw_effort: AttributePrimitive;
  }> = [];
  const validItems: Array<{ item: Item; score: number }> = [];

  for (const item of filteredItems) {
    const reach = item.attributes.reach;
    const impact = item.attributes.impact;
    const confidence = item.attributes.confidence;
    const effort = item.attributes.effort;

    // Validate required attributes (per TOOL_SPEC: reject if missing or <= 0)
    if (
      reach === null ||
      reach === undefined ||
      (typeof reach === "number" && reach <= 0)
    ) {
      continue; // Skip invalid items
    }
    if (
      impact === null ||
      impact === undefined ||
      (typeof impact === "number" && impact <= 0)
    ) {
      continue;
    }
    if (
      confidence === null ||
      confidence === undefined ||
      (typeof confidence === "number" && (confidence < 0 || confidence > (confidenceScale === "0_to_1" ? 1 : 100)))
    ) {
      continue;
    }
    if (
      effort === null ||
      effort === undefined ||
      (typeof effort === "number" && effort <= 0)
    ) {
      continue;
    }

    // Calculate RICE score
    const reachNum = typeof reach === "number" ? reach : Number(reach);
    const impactNum = typeof impact === "number" ? impact : Number(impact);
    const confidenceNum = typeof confidence === "number" ? confidence : Number(confidence);
    const effortNum = typeof effort === "number" ? effort : Number(effort);

    const normalizedConfidence = confidenceNum / confidenceDivisor;
    const score = (reachNum * impactNum * normalizedConfidence) / effortNum;

    validItems.push({ item, score });
  }

  // Sort by score (descending), then by label (ascending) for tie-breaking
  validItems.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < validItems.length; i++) {
    const { item, score } = validItems[i];
    
    // If score differs from previous, update rank
    if (i > 0 && validItems[i - 1].score !== score) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      score: score,
      rank: currentRank,
      raw_reach: item.attributes.reach,
      raw_impact: item.attributes.impact,
      raw_confidence: item.attributes.confidence,
      raw_effort: item.attributes.effort,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: validItems.length,
      confidence_input_scale: confidenceScale,
    },
  };
}

